<template>
  <div class="form-group" :class="className">
    <label :for="inputId" class="form-label">{{ labelName }}</label>
    <input
      :id="inputId"
      :aria-describedby="inputId"
      :placeholder="placeholder"
      type="text"
      :value="modelValue"
      class="form-control pj-input"
      :class="{ 'is-invalid': errorMessage }"
      @input="change($event.target.value)"
      @blur="$emit('blur', $event)"
      ref="input"
      :readonly="readonly"
      :disabled="readonly"
      :name="name"
      :maxLength="maxLength"
    />
    <div class="invalid-feedback" v-if="errorMessage">
      {{ errorMessage }}
    </div>
    <div :id="inputId" class="form-text"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, toRefs, watch, nextTick } from "vue";
import { useI18n } from "vue-i18n";
export default defineComponent({
  name: "InputFieldText",
  emits: ["update:modelValue", "blur"],
  props: {
    labelName: String,
    placeholder: String,
    inputId: String,
    className: String,
    maxLength: {
      type: Number,
      required: false,
      default: null,
    },
    modelValue: [String, Number],
    name: {
      type: String,
      default: "",
    },
    required: { type: Boolean, default: true },
    readonly: { type: Boolean, default: false },
    regex: {
      type: String,
      required: false,
      default: null,
    },
  },
  setup: (props, { emit }) => {
    const { modelValue } = toRefs(props);
    const { t } = useI18n();
    const errorMessage = ref("");
    const error = ref(false);
    const input = ref(null as HTMLInputElement | null);
    const inputValue = ref<unknown>("" as string);

    const validate = (): boolean => {
      if (!inputValue.value && props.required) {
        errorMessage.value = t("SHARED.FIELD_REQUIRED");
        error.value = true;
        return false;
      } else {
        error.value = false;
        errorMessage.value = "";
      }
      emit("update:modelValue", inputValue.value);
      return true;
    };

    watch(inputValue, (value: unknown) => {
      inputValue.value = value;
      if (value !== "" && value !== "0.00" && value !== undefined) {
        emit("update:modelValue", inputValue.value);
      }
    });

    watch(
      () => props.modelValue,
      () => {
        inputValue.value = props.modelValue;
      }
    );

    onMounted(() => (inputValue.value = modelValue.value));

    const change = (value: string) => {
      if (props?.regex && value) {
        const regexPattern = new RegExp(props.regex, "g");
        const validChars = value.match(regexPattern);
        value = validChars ? validChars.join("") : "";

        if (input.value && input.value.value !== value) {
          input.value.value = value;
        }
      }

      inputValue.value = value;
      nextTick(() => {
        validate();
        emit("update:modelValue", inputValue.value);
      });
    };

    return {
      t,
      change,
      input,
      inputValue,
      errorMessage,
      validate,
    };
  },
});
</script>
